/**
 * Image processing utilities for visual health analysis
 */

/**
 * Capture image from video stream and convert to base64
 * @param videoElement - HTML video element with active stream
 * @param quality - JPEG quality (0-1), default 0.8
 * @returns Base64 encoded image string
 */
export const captureImageFromVideo = (
    videoElement: HTMLVideoElement,
    quality: number = 0.8
): string => {
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Failed to get canvas context');
    }

    // Draw the current video frame to canvas
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64 JPEG
    return canvas.toDataURL('image/jpeg', quality);
};

/**
 * Compress a base64 image to reduce storage size
 * @param base64Image - Base64 encoded image string
 * @param maxWidth - Maximum width for the compressed image
 * @param quality - JPEG quality (0-1)
 * @returns Compressed base64 encoded image string
 */
export const compressImage = (
    base64Image: string,
    maxWidth: number = 1280,
    quality: number = 0.7
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions while maintaining aspect ratio
            if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext('2d');
            if (!context) {
                reject(new Error('Failed to get canvas context'));
                return;
            }

            context.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };

        img.onerror = () => {
            reject(new Error('Failed to load image for compression'));
        };

        img.src = base64Image;
    });
};

/**
 * Validate base64 image string
 * @param base64String - String to validate
 * @returns True if valid base64 image
 */
export const isValidBase64Image = (base64String: string): boolean => {
    if (!base64String || typeof base64String !== 'string') {
        return false;
    }

    // Check if it starts with data:image/
    const base64Pattern = /^data:image\/(jpeg|jpg|png|gif|webp);base64,/;
    return base64Pattern.test(base64String);
};

/**
 * Get image dimensions from base64 string
 * @param base64Image - Base64 encoded image string
 * @returns Promise resolving to { width, height }
 */
export const getImageDimensions = (base64Image: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            resolve({ width: img.width, height: img.height });
        };

        img.onerror = () => {
            reject(new Error('Failed to load image'));
        };

        img.src = base64Image;
    });
};